import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Palmtree, Check, Loader2 } from "lucide-react";
import DashboardShell from "@/layouts/ProviderDashboardLayout";
import api from "@/lib/api";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

export default function VacationSpecialtiesPage() {
  const [profile, setProfile] = useState(undefined);
  const [subs, setSubs] = useState([]);
  const [parent, setParent] = useState(null);
  const [sel, setSel] = useState([]);
  const [saving, setSaving] = useState(false);
  useDocumentTitle("Vacation rental specialties");

  useEffect(() => {
    api.get("/providers/me/profile").then((r) => { setProfile(r.data); setSel(r.data?.subcategory_ids || []); }).catch(() => setProfile(null));
    api.get("/categories").then((r) => {
      const p = (r.data || []).find((c) => c.slug === "vacation-rentals");
      setParent(p);
      setSubs((r.data || []).filter((c) => p && c.parent_id === p.id));
    });
  }, []);

  if (profile === undefined) return <DashboardShell><div className="flex items-center gap-2 text-[#252A34]/60"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div></DashboardShell>;
  if (!profile) return <DashboardShell><div className="rounded-2xl border bg-white p-8 text-center" data-testid="vs-no-profile">Create a provider profile first.</div></DashboardShell>;
  const vrIds = subs.map((s) => s.id);
  const toggle = (id) => setSel((x) => (x.includes(id) ? x.filter((y) => y !== id) : [...x, id]));
  const save = async () => {
    setSaving(true);
    const anyVr = sel.some((x) => vrIds.includes(x));
    const cats = (profile.category_ids || []).filter((c) => c !== parent.id).concat(anyVr ? [parent.id] : []);
    try {
      const { data } = await api.patch("/providers/me/profile", { subcategory_ids: sel, category_ids: cats });
      setProfile(data);
      toast.success("Specialties saved");
    } catch (err) { toast.error(err?.response?.data?.error || "Could not save"); } finally { setSaving(false); }
  };

  return (
    <DashboardShell>
      <div className="max-w-4xl" data-testid="vacation-specialties-page">
        <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71] inline-flex items-center gap-2"><Palmtree className="h-4 w-4" /> Vacation rentals</div>
        <h1 className="mt-1 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Your vacation rental specialties</h1>
        <p className="text-sm text-[#252A34]/65 mt-1">Hosts and managers are matched to you for the specialties you keep switched on. We pre-selected ones that fit your current services; turn off anything you don't offer.</p>
        <div className="mt-6 grid gap-2 sm:grid-cols-2">
          {subs.map((s) => {
            const on = sel.includes(s.id);
            return (
              <button key={s.id} onClick={() => toggle(s.id)} className={`rounded-2xl border p-4 text-left flex items-center gap-3 transition-colors ${on ? "border-[#165a71] bg-[#165a71]/5" : "border-slate-200 bg-white hover:border-[#165a71]/40"}`} data-testid={`vs-toggle-${s.slug}`}>
                <span className={`h-5 w-5 rounded-md border flex items-center justify-center ${on ? "bg-[#165a71] border-[#165a71] text-white" : "border-slate-300"}`}>{on && <Check className="h-3.5 w-3.5" />}</span>
                <span className="text-sm font-medium text-[#071b22] flex-1">{s.name}</span>
                {s.kind === "digital" && <span className="text-[10px] uppercase tracking-wider text-[#165a71]">Remote</span>}
              </button>
            );
          })}
        </div>
        <button onClick={save} disabled={saving} className="mt-6 rounded-full bg-[#165a71] text-white px-6 py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="vs-save">{saving ? "Saving…" : "Save specialties"}</button>
      </div>
    </DashboardShell>
  );
}
